import { authedFetch } from '@/shared/api/client';
import type { SelectOption } from '@/shared/components/AsyncSelect';
import type { PagedRequest } from '@/shared/api/usePagedQuery';

export interface Driver {
  id: string;
  name: string;
  phone: string;
  licenseNumber: string;
  licenseExpiryDate: string;
  gdlNumber?: string | null;
  gdlExpiryDate?: string | null;
  isActive: boolean;
}
export interface ServiceRecord {
  id?: string;
  serviceDate: string;
  description: string;
  odometerKm: number;
  cost: number;
  nextServiceDate?: string | null;
}
export interface Vehicle {
  id: string;
  registrationNumber: string;
  make: string;
  model: string;
  capacity: number;
  vehicleType: 'van' | 'lorry';
  lastTestDate: string | null;
  roadTaxExpiryDate: string | null;
  puspakomExpiryDate: string | null;
  lastServiceDate: string | null;
  isActive: boolean;
  serviceRecords: ServiceRecord[];
}
export interface LogisticsItem {
  id?: string;
  productId: string | null;
  logisticsProductId?: string | null;
  productName?: string;
  itemCode?: string;
  quantity: number;
  uom?: string | null;
  remarks?: string | null;
}
export interface LogisticsDelivery {
  id: string;
  number: string;
  bookingDate: string | null;
  bookedAt?: string | null;
  bookingSubmittedAtSource?:
    'source_created' | 'first_modified_fallback' | 'wms_first_seen' | null;
  latestModifiedAt?: string | null;
  fromLocation: string;
  toLocation: string;
  driverId: string | null;
  driverName: string | null;
  vehicleId: string | null;
  vehicleRegistrationNumber: string | null;
  agentId: string | null;
  agentName: string | null;
  status: string;
  remarks?: string | null;
  items: LogisticsItem[];
  sourceSystem?: string | null;
  sourceResource?: string | null;
  sourceAgentCode?: string | null;
  shipperCode?: string | null;
  sourceCancelled?: boolean | null;
  sourceCounterparty?: string | null;
  relatedDeliveryOrderNumbers?: string[] | null;
  sourceExternalReference?: string | null;
  customerName?: string | null;
  sourceSalesTeam?: string | null;
  itemCount?: number;
  bucketId?: string | null;
  bucketName?: string | null;
  lastImportedAt?: string | null;
}
export interface SearchRequest extends PagedRequest {
  search: string;
  bucketIds?: string[];
  sourceResource?: string;
  emptyDeliveryDate?: boolean;
  status?: string;
  assignmentStatus?: string;
  bookingDateFrom?: string;
  bookingDateTo?: string;
  agent?: string;
  salesTeam?: string;
}

export interface LogisticsDeliveryFilterOptions {
  agents: SelectOption[];
  salesTeams: SelectOption[];
}

export interface LogisticsBucket {
  id: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  sortOrder: number;
}

export interface LogisticsBucketRule {
  id: string;
  bucketId: string;
  bucketName?: string;
  enabled: boolean;
  priority: number;
  matchType: 'exact' | 'prefix';
  shipperPattern: string;
}

export interface LogisticsSyncSettings {
  baseUrl: string;
  configured: boolean;
  intervalMinutes: number;
  pageSize: number;
  startDate: string;
  dispatchLocation: string;
  receivingLocation: string;
  enabled: boolean;
}

export interface LogisticsSyncStatus {
  running: boolean;
  lastRunAt?: string | null;
  lastSuccessfulRunAt?: string | null;
  imported: number;
  updated: number;
  skipped: number;
  rejected: number;
  lastError?: string | null;
  lastRejectionReason?: string | null;
  checkpointCursor?: string | null;
}

export interface LogisticsSyncDocumentOutcome {
  documentNumber: string;
  sourceResource: string;
  outcome: 'skipped' | 'rejected';
  reason: string;
}

export interface LogisticsSyncValueChange {
  before: unknown;
  after: unknown;
}

export interface LogisticsSyncHistoryItem {
  lineKey: string;
  itemCode?: string | null;
  quantity: number;
  uom?: string | null;
  description?: string | null;
}

export interface LogisticsSyncHistoryEntry {
  id: string;
  runId: string;
  runStartedAt: string;
  recordedAt: string;
  deliveryId?: string | null;
  documentNumber: string;
  sourceResource: string;
  sourceKey: string;
  outcome: 'imported' | 'updated';
  beforeSourceVersion?: string | null;
  afterSourceVersion?: string | null;
  beforeSourceModifiedAt?: string | null;
  afterSourceModifiedAt?: string | null;
  bookingSubmittedAt?: string | null;
  bookingSubmittedAtSource?: LogisticsDelivery['bookingSubmittedAtSource'];
  fieldDiff: Record<string, LogisticsSyncValueChange>;
  itemDiff: {
    added: LogisticsSyncHistoryItem[];
    removed: LogisticsSyncHistoryItem[];
    changed: Array<{
      lineKey: string;
      changes: Record<string, LogisticsSyncValueChange>;
    }>;
  };
  referenceDiff: {
    added: Array<{ type: string; value: string }>;
    removed: Array<{ type: string; value: string }>;
  };
}

export interface LogisticsSyncHistoryPage {
  currentPage: number;
  pageSize: number;
  data: LogisticsSyncHistoryEntry[];
}

export interface XMLPreviewItem {
  productId: string | null;
  logisticsProductId: string | null;
  productName: string;
  itemCode: string;
  quantity: number;
  remarks: string;
  matched: boolean;
}

export interface LogisticsProduct {
  id: string;
  itemCode: string;
  name: string;
  uom?: string | null;
  remarks?: string | null;
  usages: number;
}

export interface XMLPreview {
  sourceDocumentNumber: string;
  externalReference: string;
  bookingDate: string;
  fromLocation: string;
  toLocation: string;
  agentCode: string;
  agentId: string | null;
  agentName: string;
  status: string;
  remarks: string;
  items: XMLPreviewItem[];
  warnings: string[];
}

export interface ComplianceNotification {
  id: string;
  title: string;
  message: string;
  expiryDate: string;
  daysRemaining: number;
  severity: 'warning' | 'error';
  path: string;
}

export interface AssignmentBoard {
  deliveries: LogisticsDelivery[];
  vehicles: Pick<
    Vehicle,
    'id' | 'registrationNumber' | 'make' | 'model' | 'vehicleType' | 'capacity'
  >[];
  drivers: { id: string; label: string }[];
}

export interface DeliveryAssignment {
  deliveryId: string;
  vehicleId: string | null;
  driverId: string | null;
}

export function displayLogisticsDate(value?: string | null): string {
  if (!value) return '—';
  const raw = value.slice(0, 10);
  if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

export function displayLogisticsDateTime(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kuala_Lumpur',
      }).format(parsed);
}

export function logisticsSourceLabel(source?: string | null): string {
  if (source === 'extradeliveryorder') return 'LD';
  if (source === 'extragoodsreceived') return 'GR';
  return source || 'Manual';
}

export function logisticsDateInput(value?: string | null): string {
  if (!value) return '';
  const raw = value.slice(0, 10);
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(raw);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : raw;
}

export function logisticsDateApi(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await authedFetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(detail || `${response.status} ${response.statusText}`);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

function rows<T>(value: T[] | { data?: T[] }): T[] {
  return Array.isArray(value) ? value : (value.data ?? []);
}

function crud<T, B>(base: string) {
  return {
    search: async (body: SearchRequest, signal: AbortSignal) =>
      rows(
        await request<T[] | { data?: T[] }>(`${base}/search`, {
          method: 'POST',
          body: JSON.stringify(body),
          signal,
        }),
      ),
    count: (body: SearchRequest, signal: AbortSignal) =>
      request<number>(`${base}/count`, { method: 'POST', body: JSON.stringify(body), signal }),
    getById: (id: string, signal?: AbortSignal) => request<T>(`${base}/${id}`, { signal }),
    create: (body: B) => request<T>(base, { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: B) =>
      request<void>(`${base}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: string) => request<void>(`${base}/${id}`, { method: 'DELETE' }),
    selectOptions: async (search: string, signal: AbortSignal): Promise<SelectOption[]> =>
      rows(
        await request<SelectOption[] | { data?: SelectOption[] }>(
          `${base}/select-options?SearchString=${encodeURIComponent(search)}&PageSize=20`,
          { signal },
        ),
      ),
  };
}

export type DriverInput = Omit<Driver, 'id'>;
export type VehicleInput = Omit<Vehicle, 'id' | 'lastServiceDate'>;
export type LogisticsDeliveryInput = Pick<
  LogisticsDelivery,
  'fromLocation' | 'toLocation' | 'agentId' | 'remarks' | 'items'
> & { bookingDate: string };
export interface LogisticsDeliveryOperationalInput {
  bookingDate: string | null;
  fromLocation: string;
  toLocation: string;
  remarks: string | null;
}
export const driverApi = crud<Driver, DriverInput>('/api/tms/drivers');
export const vehicleApi = crud<Vehicle, VehicleInput>('/api/tms/vehicles');
export const logisticsDeliveryApi = crud<LogisticsDelivery, LogisticsDeliveryInput>(
  '/api/tms/logistics-deliveries',
);
export const updateLogisticsDeliveryOperations = (
  id: string,
  body: LogisticsDeliveryOperationalInput,
) =>
  request<void>(`/api/tms/logistics-deliveries/${id}/operations`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
export const logisticsDeliveryFilterOptions = (signal?: AbortSignal) =>
  request<LogisticsDeliveryFilterOptions>('/api/tms/logistics-deliveries/filter-options', {
    signal,
  });
export const assignLogisticsBookingDate = (id: string, bookingDate: string) =>
  request<void>(`/api/tms/logistics-deliveries/${id}/booking-date`, {
    method: 'PUT',
    body: JSON.stringify({ bookingDate }),
  });
export const logisticsBucketApi = crud<
  LogisticsBucket,
  Pick<LogisticsBucket, 'name' | 'description' | 'sortOrder'>
>('/api/tms/logistics-buckets');
export const logisticsBucketRuleApi = crud<
  LogisticsBucketRule,
  Omit<LogisticsBucketRule, 'id' | 'bucketName'>
>('/api/tms/logistics-bucket-rules');
export const logisticsProductApi = {
  ...crud<LogisticsProduct, never>('/api/tms/logistics-products'),
  promote: (id: string, product: import('@/features/products/api').ProductCreateUpdate) =>
    request<{ productId: string }>(`/api/tms/logistics-products/${id}/promote`, {
      method: 'POST',
      body: JSON.stringify(product),
    }),
};

export const agentSelectOptions = async (
  search: string,
  signal: AbortSignal,
): Promise<SelectOption[]> =>
  rows(
    await request<SelectOption[] | { data?: SelectOption[] }>(
      `/api/tms/agents/select-options?SearchString=${encodeURIComponent(search)}`,
      { signal },
    ),
  );

export const tmsApi = {
  bucketOptions: async (signal?: AbortSignal) =>
    rows(
      await request<LogisticsBucket[] | { data?: LogisticsBucket[] }>(
        '/api/tms/logistics-buckets/options',
        { signal },
      ),
    ),
  buckets: async (signal?: AbortSignal) =>
    rows(
      await request<LogisticsBucket[] | { data?: LogisticsBucket[] }>(
        '/api/tms/logistics-buckets',
        { signal },
      ),
    ),
  bucketRules: async (signal?: AbortSignal) =>
    rows(
      await request<LogisticsBucketRule[] | { data?: LogisticsBucketRule[] }>(
        '/api/tms/logistics-bucket-rules',
        { signal },
      ),
    ),
  syncSettings: (signal?: AbortSignal) =>
    request<LogisticsSyncSettings>('/api/tms/logistics-sync/settings', { signal }),
  updateSyncSettings: (body: LogisticsSyncSettings) =>
    request<LogisticsSyncSettings>('/api/tms/logistics-sync/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  syncStatus: (signal?: AbortSignal) =>
    request<LogisticsSyncStatus>('/api/tms/logistics-sync/status', { signal }),
  syncDocumentOutcomes: (signal?: AbortSignal) =>
    request<LogisticsSyncDocumentOutcome[]>('/api/tms/logistics-sync/document-outcomes', {
      signal,
    }),
  syncHistory: (
    params: { page: number; pageSize: number; document?: string },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({
      page: String(params.page),
      pageSize: String(params.pageSize),
    });
    if (params.document) query.set('document', params.document);
    return request<LogisticsSyncHistoryPage>(
      `/api/tms/logistics-sync/history?${query.toString()}`,
      { signal },
    );
  },
  runSync: () => request<void>('/api/tms/logistics-sync/run', { method: 'POST' }),
  resetSyncDeliveries: (code: string) =>
    request<{ deleted: number }>('/api/tms/logistics-sync/reset', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  reclassifyDrafts: () =>
    request<{ updated: number }>('/api/tms/logistics-buckets/reclassify', { method: 'POST' }),
  assignmentBoard: (signal?: AbortSignal) =>
    request<AssignmentBoard>('/api/tms/logistics-deliveries/assignment-board', { signal }),
  saveAssignmentBoard: (assignments: DeliveryAssignment[]) =>
    request<void>('/api/tms/logistics-deliveries/assignment-board', {
      method: 'PUT',
      body: JSON.stringify({ assignments }),
    }),
  calendar: (from: string, to: string, signal?: AbortSignal) =>
    request<LogisticsDelivery[]>(
      `/api/tms/logistics-deliveries/calendar?from=${encodeURIComponent(logisticsDateApi(from))}&to=${encodeURIComponent(logisticsDateApi(to))}`,
      { signal },
    ),
  previewXML: (xml: string) =>
    request<XMLPreview>('/api/tms/logistics-deliveries/xml-preview', {
      method: 'POST',
      body: JSON.stringify({ xml }),
    }),
  complianceSettings: (signal?: AbortSignal) =>
    request<{ alertDays: number }>('/api/tms/compliance/settings', { signal }),
  updateComplianceSettings: (alertDays: number) =>
    request<{ alertDays: number }>('/api/tms/compliance/settings', {
      method: 'PUT',
      body: JSON.stringify({ alertDays }),
    }),
  notifications: (signal?: AbortSignal) =>
    request<ComplianceNotification[]>('/api/tms/compliance/notifications', { signal }),
};
